import { useTranslate } from "@tolgee/react";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { KrwSubtext, useIsKoreanLocale } from "#/components/krw-subtext";
import { Button } from "#/components/ui/button";
import { Dialog, DialogTrigger } from "#/components/ui/dialog";
import { isOptimisticOrder } from "#/hooks/optimistic-updates";
import { toNumber } from "#/lib/formatters";
import { cn } from "#/lib/utils";
import { useBestBidAsk } from "../../hooks/use-best-bid-ask";
import { getApiCoin } from "../../lib/hyperliquid";
import { QuickPriceButton } from "../order-form/quick-price-button";
import { sanitizeNumericInput } from "../order-form/utils/sanitize-numeric-input";
import { TradeDialogContent } from "./trade-dialog-content.tsx";
import type {
	ModifyOrderValues,
	OpenOrderRowItem,
} from "./types/positions.types.ts";

const PRICE_MAX_DECIMALS = 8;

const fieldClass =
	"flex items-center gap-1.5 rounded-[8px] border border-trade-border-subtle bg-trade-surface-inset px-2.5 py-1.5 sm:gap-2 sm:rounded-[10px] sm:px-3.5 sm:py-2";

const inputClass =
	"w-full bg-transparent text-label1-bold text-trade-text outline-none placeholder:text-trade-text-faint sm:text-body2-bold";

const labelClass = "whitespace-nowrap text-label2 sm:text-body2";

/** Trigger-market orders have no independent limit price — p follows the trigger. */
function isMarketTriggerOrder(order: OpenOrderRowItem): boolean {
	return order.isTrigger && order.orderType.endsWith("Market");
}

type OpenOrderEditCellProps = {
	order: OpenOrderRowItem;
	onModify: (order: OpenOrderRowItem, values: ModifyOrderValues) => void;
};

export function OpenOrderEditCell({ order, onModify }: OpenOrderEditCellProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const isBuy = order.side === "B";

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="xs"
					// In-flight optimistic order (negative oid) — no real oid to modify yet.
					disabled={isOptimisticOrder(order)}
					aria-label={t("orders.edit")}
					className="px-0 text-trade-text-faint hover:bg-transparent hover:text-trade-text-strong"
				>
					<Pencil className="size-3" />
				</Button>
			</DialogTrigger>
			<TradeDialogContent
				title={
					<>
						{t("orders.edit-title")} - {order.coin}-PERP -{" "}
						<span className={isBuy ? "text-trade-green" : "text-trade-red"}>
							{t(isBuy ? "orders.side.buy" : "orders.side.sell")}
						</span>
					</>
				}
			>
				{/* Form is a child so its state re-seeds from the order on every open
				    and useBestBidAsk only subscribes while the dialog is mounted. */}
				<OpenOrderEditForm
					order={order}
					onSubmit={(values) => {
						onModify(order, values);
						setOpen(false);
					}}
				/>
			</TradeDialogContent>
		</Dialog>
	);
}

function OpenOrderEditForm({
	order,
	onSubmit,
}: {
	order: OpenOrderRowItem;
	onSubmit: (values: ModifyOrderValues) => void;
}) {
	const { t } = useTranslate();
	const isKorean = useIsKoreanLocale();
	const [price, setPrice] = useState(order.limitPx);
	const [size, setSize] = useState(order.sz);
	const [triggerPrice, setTriggerPrice] = useState(order.triggerPx);

	const showLimitPrice = !isMarketTriggerOrder(order);
	const { bestBid, bestAsk } = useBestBidAsk(getApiCoin(order.coin));

	const priceNumeric = toNumber(price);
	const showPriceKrw = isKorean && showLimitPrice && priceNumeric > 0;

	const canSubmit =
		toNumber(size) > 0 &&
		(!showLimitPrice || priceNumeric > 0) &&
		(!order.isTrigger || toNumber(triggerPrice) > 0);

	return (
		<form
			className="mt-4"
			onSubmit={(e) => {
				e.preventDefault();
				if (!canSubmit) return;
				onSubmit({ price, size, triggerPrice });
			}}
		>
			{order.isTrigger && (
				<label className={cn(fieldClass, "mb-2")}>
					<div className="flex w-full items-center gap-2 sm:gap-3">
						<p className={labelClass}>{t("orders.columns.price")}</p>
						<input
							type="text"
							inputMode="decimal"
							className={cn(inputClass, "text-right")}
							value={triggerPrice}
							onChange={(e) =>
								setTriggerPrice(
									sanitizeNumericInput(e.target.value, PRICE_MAX_DECIMALS),
								)
							}
							placeholder="0"
						/>
					</div>
				</label>
			)}
			{showLimitPrice && (
				<label className={cn(fieldClass, "mb-2")}>
					<div className="flex w-full items-center gap-2 sm:gap-3">
						<p className={labelClass}>{t("positions.limit-price")}</p>
						<div className="flex-1">
							<input
								type="text"
								inputMode="decimal"
								className={cn(inputClass, "text-right")}
								value={price}
								onChange={(e) =>
									setPrice(
										sanitizeNumericInput(e.target.value, PRICE_MAX_DECIMALS),
									)
								}
								placeholder="0"
							/>
							{showPriceKrw && (
								<div className="flex justify-end">
									<KrwSubtext
										usd={priceNumeric}
										className="text-caption1 sm:text-label2"
									/>
								</div>
							)}
						</div>
					</div>
				</label>
			)}
			<label className={cn(fieldClass, "mb-2")}>
				<div className="flex w-full items-center gap-2 sm:gap-3">
					<p className={labelClass}>{t("orders.columns.size")}</p>
					<div className="flex w-full flex-1 items-center gap-1.5">
						<input
							type="text"
							inputMode="decimal"
							className={cn(inputClass, "text-right")}
							value={size}
							onChange={(e) =>
								setSize(
									sanitizeNumericInput(e.target.value, PRICE_MAX_DECIMALS),
								)
							}
							placeholder="0"
						/>
						<span className="whitespace-nowrap text-label2 text-trade-text-faint">
							{order.coin}
						</span>
					</div>
				</div>
			</label>
			{showLimitPrice && (
				<div className="mb-2 flex gap-1.5">
					<QuickPriceButton
						side="buy"
						price={bestBid}
						showPrice
						compactOnMobile={false}
						onApplyPrice={setPrice}
						className="flex-1"
					/>
					<QuickPriceButton
						side="sell"
						price={bestAsk}
						showPrice
						compactOnMobile={false}
						onApplyPrice={setPrice}
						className="flex-1"
					/>
				</div>
			)}
			<Button
				type="submit"
				size="sm"
				disabled={!canSubmit}
				className="mt-2 w-full text-[var(--color-theme-static-white)]"
			>
				{t("orders.edit-submit")}
			</Button>
		</form>
	);
}
